import { FC, Suspense, useEffect, useRef, useState } from 'react'
import mapboxgl, { LngLatBounds } from 'mapbox-gl'
import throttle from 'lodash/throttle'
import 'mapbox-gl/dist/mapbox-gl.css'
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import { MAPBOX_STYLES, MAPBOX_TOKEN, MAP_DEFAULT_STYLE } from 'src/components/map/mapbox/config'
import { Bounds, MapStyle } from 'src/types/map'
import styled from '@emotion/styled'
import { isGMapBoundsEqual } from 'src/components/map/utils'
import { atom, useRecoilCallback, useRecoilValue } from 'recoil'
import { TILE_AUTH_HEADER_KEY, TILE_VERIFY_HEADER_KEY } from 'src/constants/map-tiles'
import * as searchAtoms from 'src/atoms/search'
import { useAfterMountEffect } from 'src/hooks/use-after-mount-effect'
import { Position } from 'src/common-services'
import { MapboxMapContext } from 'src/hooks/use-mapbox-map'
import { loadImages } from 'src/utils/mapbox-main-map'
import { browserLogger } from 'src/utils/browser-logger'
import { AERIAL_TOGGLE_ZOOM_THRESHOLD } from 'src/components/map/google-map/map-type-toggle'

import * as tilesAtoms from 'src/atoms/tiles'
import * as reportInsightAtoms from 'src/atoms/report-insight'
import * as mainMapAtoms from 'src/atoms/main-map'

const Container = styled.div`
  background: #e8eaed;
  flex: 1 1 auto;
  position: relative;

  .mapboxgl-ctrl-bottom-left {
    display: none !important;
  }
`

const MapImageRemplacement = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
`

type Props = {
  zoom: number
  bounds: Bounds
  onBoundsChange?: (bounds: Bounds) => void
  onZoomChange?: (zoom: number) => void
  flyTo?: Position | Bounds
  children?: React.ReactNode
  style?: MapStyle
  isStatic?: boolean
  disablePOIClick?: boolean
  hideZoomControl?: boolean
  noTiles?: boolean
}

const DEFAULT_ZOOM = 13
const FOCUS_ZOOM = 18

const noopAtom = atom<undefined>({
  key: 'mc-noop',
  default: undefined,
})

const Inner: FC<Props> = ({
  bounds,
  zoom,
  onBoundsChange,
  onZoomChange,
  flyTo,
  children,
  style = MAP_DEFAULT_STYLE,
  isStatic,
  disablePOIClick,
  hideZoomControl,
  noTiles = false,
}) => {
  const [map, setMap] = useState<mapboxgl.Map | null>(null)
  const [mapImage, setMapImage] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const isLocationSelector = useRecoilValue(searchAtoms.isLocationSelector)
  const auth = useRecoilValue(noTiles ? noopAtom : tilesAtoms.auth)
  const isMapStatic = useRecoilValue(reportInsightAtoms.isMapStatic)
  const contents = useRecoilValue(searchAtoms.googleSearchResult)
  const zoomRef = useRef(zoom)
  const boundsRef = useRef(bounds)
  const isAerialEnabled = useRecoilValue(mainMapAtoms.isAerialEnabled)

  const searchPOI = useRecoilCallback(({ set }) => (txt: string) => {
    set(searchAtoms.internalGooglePlaceId, undefined)
    set(searchAtoms.googleSearchString, txt)
    set(searchAtoms.selectedLocationId, undefined)
  })

  const screenshotMap = () => {
    if (!map) {
      return
    }
    map.once('render', function () {
      const canvas = map.getCanvas()
      const img = canvas.toDataURL('image/png')
      setMapImage(img)
    })
    // this is needed to force a rerender of the map
    map.setBearing(map.getBearing())
  }

  useEffect(() => {
    if (!map || !contents || contents.length === 0) {
      return
    }

    const bounds = map.getBounds()
    const mapZoom = map.getZoom()
    const offsetInDegrees = 1 / 140 // 1 mile is 1/69 degrees aproximately
    const extendedBounds = new mapboxgl.LngLatBounds(
      new mapboxgl.LngLat(bounds.getWest() - offsetInDegrees, bounds.getSouth() - offsetInDegrees),
      new mapboxgl.LngLat(bounds.getEast() + offsetInDegrees, bounds.getNorth() + offsetInDegrees)
    )

    for (const location of contents) {
      const lng = location.geometry.location[1]
      const lat = location.geometry.location[0]
      if (extendedBounds.contains([lat, lng])) {
        map.flyTo({ center: [lat, lng], zoom: mapZoom < DEFAULT_ZOOM ? DEFAULT_ZOOM : mapZoom })
        return
      }
    }

    const zoom = contents.length === 1 ? (mapZoom < DEFAULT_ZOOM ? FOCUS_ZOOM : mapZoom) : DEFAULT_ZOOM
    const lng = contents[0].geometry.location[1]
    const lat = contents[0].geometry.location[0]
    map.flyTo({ center: [lat, lng], zoom })
  }, [contents])

  useEffect(() => {
    if (
      !map ||
      !bounds ||
      bounds.center.length === 0 ||
      bounds.northEast.length === 0 ||
      bounds.southWest.length === 0
    ) {
      return
    }

    const currentBounds = map.getBounds()
    const currentNE = currentBounds.getNorthEast()
    const currentSW = currentBounds.getSouthWest()

    const isEqual =
      currentNE.lng === bounds.northEast[0] &&
      currentNE.lat === bounds.northEast[1] &&
      currentSW.lng === bounds.southWest[0] &&
      currentSW.lat === bounds.southWest[1]

    if (isEqual || !bounds.northEast.length || !bounds.southWest.length) {
      return
    }

    // Fit map to new bounds
    map.fitBounds(new LngLatBounds([bounds.northEast as [number, number], bounds.southWest as [number, number]]), {
      duration: 500, // Match the animation duration used in flyTo effect
    })
  }, [bounds, map])

  useEffect(() => {
    if (!isStatic && !isMapStatic) {
      setMapImage(null)
      return
    }
    screenshotMap()
  }, [isStatic, isMapStatic])

  useEffect(() => {
    if (!containerRef.current) {
      return
    }

    mapboxgl.accessToken = MAPBOX_TOKEN
    const _style = MAPBOX_STYLES[style]

    const map = new mapboxgl.Map({
      attributionControl: false, // removes mapbox attribution (bottom right watermark)
      container: containerRef.current,
      style: _style,
      center: bounds.center as [number, number],
      zoom: zoom ?? DEFAULT_ZOOM,
      dragPan: true,
      fadeDuration: 0, // this prevents flickering when hovering POIs
      transformRequest: (url: string, resourceType: string) => {
        if (auth == undefined) {
          return {
            url,
          }
        }

        const ourOrigin = new URL(auth.host).origin
        // this gets called with every single url that mapbox loads
        // including images, styles, etc...we only want to include our token with tile requests
        if (resourceType === 'Tile' && url.startsWith(ourOrigin)) {
          return {
            url,
            headers: {
              [TILE_AUTH_HEADER_KEY]: auth.token,
              [TILE_VERIFY_HEADER_KEY]: auth.verify,
            },
          }
        }
        return {
          url,
        }
      },
    })

    map.getCanvas().setAttribute('data-sl', 'canvas-mq') // this is used by Smartlook to record the map canvas

    if (!isLocationSelector) {
      map.dragRotate.disable()
    }

    if (!hideZoomControl) {
      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right')
    }

    map.on('load', () => {
      setMap(map)
    })

    return () => {
      map.remove()
    }
  }, [setMap, isLocationSelector, auth, hideZoomControl])

  useEffect(() => {
    const animationDuration = 500

    if (!map || !flyTo) {
      return
    }

    if (Array.isArray(flyTo)) {
      const mapZoom = map.getZoom()
      const zoom = mapZoom < DEFAULT_ZOOM ? FOCUS_ZOOM : mapZoom

      map.flyTo({
        center: flyTo as [number, number],
        duration: animationDuration,
        zoom,
      })
    } else {
      map.fitBounds(new LngLatBounds([flyTo.northEast as [number, number], flyTo.southWest as [number, number]]), {
        duration: animationDuration,
        padding: 100,
      })
    }
  }, [flyTo, map])

  const syncBounds = throttle(() => {
    if (!map) {
      return
    }

    const newBounds = map.getBounds()
    if (!newBounds) {
      return
    }
    const center = [newBounds.getCenter().lng, newBounds.getCenter().lat]
    const northEast = [newBounds.getNorthEast().lng, newBounds.getNorthEast().lat]
    const southWest = [newBounds.getSouthWest().lng, newBounds.getSouthWest().lat]
    const newBoundsObj = { center, northEast, southWest }
    // This prevent bounds changes when the bounds changed a littlebit but the map didn't move
    if (!isGMapBoundsEqual(boundsRef.current, newBoundsObj)) {
      boundsRef.current = newBoundsObj
      onBoundsChange?.(newBoundsObj)
    }
  }, 1000)

  useAfterMountEffect(() => {
    if (!map) {
      return
    }

    map.on('moveend', () => {
      syncBounds()

      const newZoom = map.getZoom()
      if (zoomRef.current !== newZoom) {
        zoomRef.current = newZoom
        onZoomChange?.(newZoom)
      }
    })
  }, [syncBounds, map])

  useEffect(() => {
    if (!map || disablePOIClick) {
      return
    }

    const layers =
      style === MapStyle.STREETS
        ? ['poi-label', 'transit-label', 'landuse', 'national-park']
        : ['poi-label', 'transit-label']

    const onClick = (
      e: mapboxgl.MapMouseEvent & {
        features?: mapboxgl.MapboxGeoJSONFeature[] | undefined
      } & mapboxgl.EventData
    ) => {
      const features = e.features
      if (features?.length) {
        const [feature] = features
        if (feature.properties && 'name' in feature.properties) {
          searchPOI(feature.properties.name)
        }
      }
    }

    map.on('click', layers, onClick)

    return () => {
      map.off('click', layers, onClick)
    }
  }, [map, style, disablePOIClick])

  useAfterMountEffect(() => {
    if (!map) {
      return
    }
    map.setZoom(zoom)
  }, [zoom])

  useAfterMountEffect(() => {
    if (!map) {
      return
    }
    map.setStyle(MAPBOX_STYLES[style])
  }, [style])

  useAfterMountEffect(() => {
    if (!map) {
      return
    }

    if (!isAerialEnabled) {
      map.setMinZoom(undefined)
      return
    }
    map.setMinZoom(AERIAL_TOGGLE_ZOOM_THRESHOLD)
  }, [isAerialEnabled])

  return (
    <Container ref={containerRef}>
      {map && (
        <MapboxMapContext.Provider value={{ map, style }}>
          {mapImage && <MapImageRemplacement src={mapImage} />}
          {children}
          <Images map={map} style={MAPBOX_STYLES[style]} />
        </MapboxMapContext.Provider>
      )}
    </Container>
  )
}

type ImagesProps = { map: mapboxgl.Map; style: string | undefined }
function Images({ map, style }: ImagesProps) {
  const styleRef = useRef(map.getStyle().name)
  const [currentStyle, setCurrenStyle] = useState(styleRef.current)

  map.on('styledata', () => {
    const newStyleName = map.getStyle().name
    if (newStyleName !== styleRef.current) {
      styleRef.current = newStyleName
      setCurrenStyle(newStyleName)
    }
  })

  useEffect(() => {
    if (!style) {
      return
    }
    if (!map) {
      return
    }
    try {
      loadImages(map)
    } catch (e) {
      browserLogger.info('Error loading images...probably during hot reload')
    }
  }, [currentStyle, map])

  return null
}

export const MapInner: FC<Props> = (props) => {
  return (
    <Suspense fallback={null}>
      <Inner {...props} />
    </Suspense>
  )
}
